import { Box, HStack, IconButton, Spinner, Textarea, VStack, useDisclosure } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRedeemTokenRewardMutation } from 'hooks/mutations/useRedeemTokenRewardMutation';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import { useDevSandbox } from 'components/DevSandbox/DevSandboxProvider';
import { useSignIn } from 'components/GitHubButton';
import Icon from 'components/Icon';
import { TokensModal } from 'components/Modal/TokensModal';
import Text from 'components/Text';
import { DEMO_GREETING, DEMO_SPELLS, buildDemoFollowUpMessage, getRandomSpellIndex } from 'features/demo/merlinDemoSpells';
import { CompleteCardParams, EdgeFunctionName, MerlinCardInfo, MerlinCodeChange, MerlinDiffInfo, MerlinMode, MerlinRequest } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

import MerlinMessageComponent from './MerlinMessage';
import { resolveCardAction, stripCodeFromOlderHistory } from './merlinUtils';
import { useMerlinMessages } from './useMerlinMessages';
import { useMerlinStream } from './useMerlinStream';
import DevSandboxTooltip from '../DevSandboxTooltip';

const MERLIN_STREAMING_EVENT = 'merlin-streaming';

const SLASH_COMMANDS: { command: string; mode: MerlinMode; label: string; description: string }[] =
  [
    {
      command: '/solution',
      mode: 'solution',
      label: 'Generate solution',
      description: 'Generate a full code solution',
    },
    {
      command: '/review',
      mode: 'review',
      label: 'Review my code',
      description: 'Get a structured code review',
    },
    {
      command: '/feedback',
      mode: 'feedback',
      label: 'Give me feedback',
      description: 'Design, UX/UI & accessibility feedback',
    },
    {
      command: '/roast',
      mode: 'roast',
      label: 'Roast my code',
      description: 'Humorous critique of your code',
    },
    {
      command: '/explain',
      mode: 'explain',
      label: 'Explain this',
      description: 'Explain concepts without spoilers',
    },
    { command: '/clear', mode: 'vibe', label: 'Clear', description: 'Clear conversation history' },
  ];

// Commands available per entity type
// - practice has no /feedback (no design)
// - /solution only for project, challenge, practice (not prototype/codeframe)
const SOLUTION_ENTITY_TYPES = ['project', 'challenge', 'practice'];

const getAvailableCommands = (entityType: string) => {
  return SLASH_COMMANDS.filter((cmd) => {
    if (cmd.command === '/feedback' && entityType === 'practice') return false;
    if (cmd.command === '/solution' && !SOLUTION_ENTITY_TYPES.includes(entityType)) return false;
    return true;
  });
};

const buildCommandsSection = (entityType: string): string => {
  const commands = getAvailableCommands(entityType).filter((cmd) => cmd.command !== '/clear');
  const lines = commands.map((cmd) => `- **${cmd.command}** — ${cmd.description}`);
  return `\n\n**Commands:**\n${lines.join('\n')}\n- **/clear** — clear conversation history\n\n---\n\nOr just start chatting and let's vibe code something together!`;
};

const HARDCODED_GREETINGS: Record<string, (name: string) => string> = {
  project: (name) =>
    `Welcome to **${name}**! I can see the design and I'm ready to help you bring it to life. Let's build something amazing together!`,
  challenge: (name) =>
    `Ready to tackle **${name}**! I've got the design reference loaded up — let's craft a pixel-perfect solution together!`,
  prototype: (name) =>
    `Welcome to **${name}**! The canvas is yours — let's explore some creative possibilities and build something great!`,
  codeframe: (name) =>
    `Welcome to **${name}**! Time to get creative — let's code something awesome together!`,
  practice: (name) =>
    `Ready for **${name}**! Let's work through this puzzle together — I'm here to help whenever you need a hint!`,
  demo: () =>
    `Hey, you've got the full powers unlocked! This demo is fun and all, but the real magic happens in your dashboard — go pick a project and let's build something serious together! 🚀`,
};

interface IMerlinPanelProps {
  entityName: string;
  entityType: string;
  entityDescription?: string;
  designScreenshotUrl?: string;
  acceptanceCriteria?: string[];
  taskCards?: string[];
  currentCard?: MerlinCardInfo | null;
  cards?: MerlinCardInfo[];
  projectScreenName?: string;
  userProjectId?: string | null;
  isDemo?: boolean;
  autoTrigger?: { message: string; mode: string; bypassAiLimit?: boolean };
  isCustomTemplate?: boolean;
}

const MerlinPanel = ({
  entityName,
  entityType,
  entityDescription,
  designScreenshotUrl,
  acceptanceCriteria,
  taskCards,
  currentCard,
  cards,
  projectScreenName,
  userProjectId,
  isDemo,
  autoTrigger,
  isCustomTemplate,
}: IMerlinPanelProps) => {
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const [showCommands, setShowCommands] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const { id: currentUserId, isFree, isSuperUser, aiPercentUsed, isAiLimitReached, bigTokensCollected } = useCurrentUser();
  const {
    codeTabs: { html: rawHtml, css: rawCss, js: rawJs },
    handlers: { onHtmlChange, onCssChange, onJsChange, onCodeTabIndexChange },
    merlin: { onClose: closeMerlin, config: merlinContextConfig },
    editorSettings,
    demoSpell: { state: demoSpellState, setState: setDemoSpellState, cancelRef: demoSpellCancelledRef, timeoutsRef: demoSpellTimeoutsRef, revert: revertDemoSpell },
  } = useDevSandbox();

  const redeemMutation = useRedeemTokenRewardMutation();
  const { isOpen: isTokensModalOpen, onOpen: onTokensModalOpen, onClose: onTokensModalClose } = useDisclosure();
  const hasTokens = bigTokensCollected > 0;
  const { handleLoginClick } = useSignIn();

  const html = rawHtml || '';
  const css = rawCss || '';
  const js = rawJs || '';

  const { messages, addMessage, updateMessage, clearMessages } = useMerlinMessages();

  // Accumulator ref for streaming text
  const streamTextRef = useRef('');
  const streamMessageIdRef = useRef('');
  const codeChangesRef = useRef<MerlinCodeChange[]>([]);
  const codeSnapshotRef = useRef<{ html: string; css: string; js: string }>({
    html: '',
    css: '',
    js: '',
  });
  const diffsRef = useRef<MerlinDiffInfo[]>([]);

  const isSignedIn = !!currentUserId;
  const isPaidUser = isSignedIn && (!isFree || isSuperUser);

  // Card completion from within Merlin messages
  const supabase = useSupabaseTypedClient();
  const { toastSuccess, toastError } = useToastWrapper();
  const [completedViaIds, setCompletedViaIds] = useState<Set<string>>(new Set());

  const handleCardComplete = useCallback(
    async (cardId: string) => {
      try {
        await supabase.functions.invoke<string>(EdgeFunctionName.CompleteCard, {
          body: { card_id: cardId, is_completed: false, user_project_id: userProjectId } as CompleteCardParams,
        });

        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, currentUserId] });
        if (projectScreenName) {
          queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProject, projectScreenName] });
        }
        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetUserAchievements, currentUserId] });

        setCompletedViaIds((prev) => { const next = new Set(prev); next.add(cardId); return next; });
        toastSuccess('Card completed!');
      } catch {
        toastError('Could not complete the card. Try again.');
      }
    },
    [supabase, userProjectId, projectScreenName, currentUserId, queryClient, toastSuccess, toastError]
  );

  // Auto-trigger: tracks whether the auto-trigger has fired (prevents re-firing)
  const autoTriggeredRef = useRef(false);

  const streamingTabsRef = useRef<Set<'html' | 'css' | 'js'>>(new Set());

  const {
    sendMessage: streamSend,
    cancelStream,
    isStreaming,
  } = useMerlinStream({
    onText: (content) => {
      streamTextRef.current += content;

      if (streamMessageIdRef.current) {
        const tabs = Array.from(streamingTabsRef.current) as ('html' | 'css' | 'js')[];
        updateMessage(streamMessageIdRef.current, {
          message: streamTextRef.current.trim(),
          status: 'streaming',
          isGeneratingCode: false,
          streamingTabs: tabs.length > 0 ? tabs : undefined,
        });
      }
    },
    onCodeStart: () => {
      // Immediately show loading indicator when backend starts generating a code block
      if (streamMessageIdRef.current) {
        updateMessage(streamMessageIdRef.current, {
          isGeneratingCode: true,
        });
      }
    },
    onCode: (codeChange) => {
      codeChangesRef.current = [...codeChangesRef.current, codeChange];

      // Track which tabs have received code
      streamingTabsRef.current.add(codeChange.tab);

      // Calculate diff before applying
      const oldCode = codeSnapshotRef.current[codeChange.tab] || '';
      const newCode =
        codeChange.action === 'append' ? oldCode + '\n' + codeChange.content : codeChange.content;
      const oldLines = oldCode.split('\n').length;
      const newLines = newCode.split('\n').length;
      const added = Math.max(0, newLines - oldLines);
      const removed = Math.max(0, oldLines - newLines);
      // For replace with both add and remove, show actual counts
      const diff: MerlinDiffInfo =
        codeChange.action === 'replace'
          ? { tab: codeChange.tab, added: newLines, removed: oldCode ? oldLines : 0 }
          : { tab: codeChange.tab, added, removed };
      // Replace existing diff for same tab or add new one
      const existingIndex = diffsRef.current.findIndex((d) => d.tab === codeChange.tab);
      if (existingIndex >= 0) {
        diffsRef.current[existingIndex] = diff;
      } else {
        diffsRef.current.push(diff);
      }

      // Auto-apply code changes
      if (codeChange.action === 'replace') {
        if (codeChange.tab === 'html') onHtmlChange(codeChange.content);
        else if (codeChange.tab === 'css') onCssChange(codeChange.content);
        else if (codeChange.tab === 'js') onJsChange(codeChange.content);
      } else if (codeChange.action === 'append') {
        if (codeChange.tab === 'html') onHtmlChange(html + '\n' + codeChange.content);
        else if (codeChange.tab === 'css') onCssChange(css + '\n' + codeChange.content);
        else if (codeChange.tab === 'js') onJsChange(js + '\n' + codeChange.content);
      }

      // Update message with code changes and diffs
      if (streamMessageIdRef.current) {
        updateMessage(streamMessageIdRef.current, {
          codeChanges: codeChangesRef.current,
          diffs: [...diffsRef.current],
          isGeneratingCode: false,
        });

        // Broadcast diffs to CodeEditor tabs
        window.dispatchEvent(
          new CustomEvent(MERLIN_STREAMING_EVENT, {
            detail: { isStreaming: true, diffs: [...diffsRef.current] },
          })
        );
      }
    },
    onDone: (tokensUsed, _remainingTokens) => {
      if (streamMessageIdRef.current) {
        const finalText = streamTextRef.current.trim();

        // Snapshot card action at finalization time so it survives currentCard changes
        // Uses fallback: if code was generated for a card but marker is missing, still show button
        const resolvedCardAction =
          resolveCardAction(finalText, currentCard, codeChangesRef.current.length > 0) ?? undefined;

        updateMessage(streamMessageIdRef.current, {
          status: 'received',
          message: finalText,
          tokensUsed,
          codeChanges: codeChangesRef.current,
          diffs: [...diffsRef.current],
          streamingTabs: undefined,
          isGeneratingCode: false,
          cardAction: resolvedCardAction,
        });
      }
      streamTextRef.current = '';
      streamMessageIdRef.current = '';
      codeChangesRef.current = [];
      diffsRef.current = [];
      streamingTabsRef.current.clear();

      // Refresh user data (AI usage %, BigTokens, storage) after AI request
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser] });
      queryClient.invalidateQueries({
        queryKey: [TanstackQueryName.GetOnboardingMissions, currentUserId],
      });

      // Auto-save after auto-triggered streaming completes
      if (autoTriggeredRef.current) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('bds:save-shortcut'));
        }, 1000);
        autoTriggeredRef.current = false;
      }
    },
    onCancel: () => {
      if (streamMessageIdRef.current) {
        const currentText = streamTextRef.current.trim();
        updateMessage(streamMessageIdRef.current, {
          status: 'received',
          message: currentText
            ? currentText + '\n\n*Generation stopped.*'
            : '*Generation stopped.*',
          codeChanges: codeChangesRef.current,
          diffs: [...diffsRef.current],
          streamingTabs: undefined,
          isGeneratingCode: false,
        });
      }
      streamTextRef.current = '';
      streamMessageIdRef.current = '';
      codeChangesRef.current = [];
      diffsRef.current = [];
      streamingTabsRef.current.clear();
    },
    onError: (errorMessage) => {
      if (streamMessageIdRef.current) {
        updateMessage(streamMessageIdRef.current, {
          status: 'error',
          message: errorMessage,
        });
      } else {
        // Error before stream started
        addMessage({
          id: uuidv4(),
          created_at: new Date().toISOString(),
          who: 'Merlin',
          message: errorMessage,
          status: 'error',
        });
      }
      streamTextRef.current = '';
      streamMessageIdRef.current = '';
      codeChangesRef.current = [];
      diffsRef.current = [];
      streamingTabsRef.current.clear();
    },
  });

  // Broadcast streaming state for VibeCodeButton notification + CodeEditor tabs
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(MERLIN_STREAMING_EVENT, {
        detail: {
          isStreaming,
          diffs: isStreaming ? [...diffsRef.current] : [],
        },
      })
    );
  }, [isStreaming]);

  // Auto scroll
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Filtered slash commands for autocomplete — scoped to entity type
  const availableCommands = useMemo(() => getAvailableCommands(entityType), [entityType]);

  const filteredCommands = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const query = input.toLowerCase();
    return availableCommands.filter((cmd) => cmd.command.startsWith(query));
  }, [input, availableCommands]);

  useEffect(() => {
    const shouldShow = input.startsWith('/') && filteredCommands.length > 0 && !isStreaming;
    setShowCommands(shouldShow);
    if (shouldShow) setSelectedCommandIndex(0);
  }, [input, filteredCommands, isStreaming]);

  const dismissCommands = useCallback(() => {
    setShowCommands(false);
    setInput('');
  }, []);

  // Close slash commands on Escape even when input is not focused
  useEffect(() => {
    if (!showCommands) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissCommands();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [showCommands, dismissCommands]);

  const buildRequest = (userMessage: string, mode: MerlinMode, bypassAiLimit?: boolean): MerlinRequest => {
    // Build conversation history from recent messages (last 20)
    const rawHistory = messages
      .filter((m) => m.status === 'received' && m.message)
      .slice(-20)
      .map((m) => ({
        role: (m.who === 'You' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: m.message,
      }));

    // Strip code blocks from older messages to save tokens
    const conversationHistory = stripCodeFromOlderHistory(rawHistory);

    const completedCount = cards?.filter((c) => c.status === 'completed').length ?? 0;
    const totalCount = cards?.length ?? 0;
    const cardsSummary =
      currentCard && cards
        ? `${completedCount}/${totalCount} completed. Current: Card ${currentCard.index + 1} — ${currentCard.title}`
        : undefined;

    return {
      mode,
      message: userMessage,
      ...(bypassAiLimit ? { bypassAiLimit: true } : {}),
      context: {
        html,
        css,
        js,
        entityType,
        entityName,
        entityDescription,
        designScreenshotUrl,
        acceptanceCriteria,
        taskCards,
        currentCard: currentCard
          ? {
              id: currentCard.id,
              title: currentCard.title,
              description: currentCard.description,
              screenshot_url: currentCard.screenshot_url,
              index: currentCard.index,
              totalCards: totalCount,
            }
          : undefined,
        cardsSummary,
        frameworkSettings: {
          framework: editorSettings.framework,
          cssMode: editorSettings.cssMode,
          packages: editorSettings.packages,
        },
      },
      conversationHistory,
    };
  };

  const handleSend = (overrideMessage?: string, overrideMode?: MerlinMode, bypassAiLimit?: boolean) => {
    const userText = overrideMessage || input.trim();
    if (!userText || isStreaming) return;

    // Handle /clear command locally
    if (userText === '/clear') {
      clearMessages();
      setInput('');
      return;
    }

    // Determine mode from slash command
    let mode: MerlinMode = overrideMode || 'vibe';
    let displayMessage = userText;

    const slashMatch = SLASH_COMMANDS.find((cmd) => userText.startsWith(cmd.command));
    if (slashMatch && slashMatch.command !== '/clear') {
      mode = slashMatch.mode;
      const extraText = userText.slice(slashMatch.command.length).trim();
      displayMessage = extraText || `\`${slashMatch.command}\``;
    }

    // Add user message
    addMessage({
      id: uuidv4(),
      created_at: new Date().toISOString(),
      who: 'You',
      message: displayMessage,
      status: 'received',
    });

    // Demo mode: simulate streaming with magic spell (all users — anon, free, paid)
    if (isDemo) {
      setInput('');
      setShowCommands(false);

      // Cancel any in-flight spell streaming before starting a new one
      demoSpellCancelledRef.current = true;
      demoSpellTimeoutsRef.current.forEach(clearTimeout);
      demoSpellTimeoutsRef.current = [];
      demoSpellCancelledRef.current = false;

      const randomIdx = getRandomSpellIndex(demoSpellState.spellIndex);
      const spell = DEMO_SPELLS[randomIdx];

      const merlinId = uuidv4();
      addMessage({
        id: merlinId,
        created_at: new Date().toISOString(),
        who: 'Merlin',
        message: '',
        status: 'sending',
      });

      // Always restore to original template code (not live editor state)
      const originalCode = merlinContextConfig?.originalCode || { html, css, js };

      // Mark spell as loading (preview shows overlay), NOT active yet
      setDemoSpellState(() => ({
        isSpellActive: false,
        isSpellLoading: true,
        savedCode: originalCode,
        spellIndex: randomIdx,
      }));

      // Stream chat text character by character (10ms per char for snappy demo feel)
      let charIndex = 0;
      const streamText = () => {
        if (demoSpellCancelledRef.current) return;
        if (charIndex < spell.text.length) {
          const partial = spell.text.slice(0, charIndex + 1);
          updateMessage(merlinId, { message: partial, status: 'streaming' });
          charIndex++;
          demoSpellTimeoutsRef.current.push(setTimeout(streamText, 10));
        } else {
          if (demoSpellCancelledRef.current) return;
          // Typing done — apply magic code
          const diffs: MerlinDiffInfo[] = [
            { tab: 'html', added: spell.html.split('\n').length, removed: html.split('\n').length },
            { tab: 'css', added: spell.css.split('\n').length, removed: css.split('\n').length },
            { tab: 'js', added: spell.js.split('\n').length, removed: js.split('\n').length },
          ];

          window.dispatchEvent(
            new CustomEvent(MERLIN_STREAMING_EVENT, { detail: { isStreaming: true, diffs } })
          );

          onHtmlChange(spell.html);
          onCssChange(spell.css);
          onJsChange(spell.js);

          updateMessage(merlinId, {
            message: spell.text,
            status: 'received',
            diffs,
            codeChanges: [
              { tab: 'html', action: 'replace', content: spell.html },
              { tab: 'css', action: 'replace', content: spell.css },
              { tab: 'js', action: 'replace', content: spell.js },
            ],
          });

          // Reveal immediately — the 600ms CSS fade-out in DemoPreview covers iframe rebuild time
          setDemoSpellState((prev) => ({ ...prev, isSpellActive: true, isSpellLoading: false }));

          demoSpellTimeoutsRef.current.push(setTimeout(() => {
            if (demoSpellCancelledRef.current) return;
            window.dispatchEvent(
              new CustomEvent(MERLIN_STREAMING_EVENT, { detail: { isStreaming: false, diffs: [] } })
            );
          }, 500));

          // Follow-up message with CTA + Remove Spell (no auto-revert)
          demoSpellTimeoutsRef.current.push(setTimeout(() => {
            if (demoSpellCancelledRef.current) return;
            addMessage({
              id: uuidv4(),
              created_at: new Date().toISOString(),
              who: 'Merlin',
              message: buildDemoFollowUpMessage(isSignedIn, isPaidUser),
              status: 'received',
              showRemoveSpell: true,
            });
          }, 500));
        }
      };

      // Start after a brief "sending" delay
      demoSpellTimeoutsRef.current.push(setTimeout(streamText, 300));
      return;
    }

    // Save snapshot for diff calculation
    codeSnapshotRef.current = { html, css, js };

    // Add Merlin placeholder
    const merlinId = uuidv4();
    addMessage({
      id: merlinId,
      created_at: new Date().toISOString(),
      who: 'Merlin',
      message: '',
      status: 'sending',
    });

    streamTextRef.current = '';
    streamMessageIdRef.current = merlinId;
    codeChangesRef.current = [];
    diffsRef.current = [];
    streamingTabsRef.current.clear();

    setInput('');
    setShowCommands(false);

    const request = buildRequest(userText, mode, bypassAiLimit);

    // Solution mode for projects = full project generation, strip card context
    if (mode === 'solution' && entityType === 'project') {
      delete request.context.currentCard;
      delete request.context.cardsSummary;
    }

    streamSend(request);
  };

  const handleTabSwitch = useCallback(
    (tab: 'html' | 'css' | 'js') => {
      const tabIndexMap = { html: 0, css: 1, js: 2 };
      onCodeTabIndexChange(tabIndexMap[tab]);
      closeMerlin();
    },
    [onCodeTabIndexChange, closeMerlin]
  );

  // Remove spell: explicitly revert code to original template, then close panel
  const handleRemoveSpell = useCallback(() => {
    revertDemoSpell();
    closeMerlin();
  }, [revertDemoSpell, closeMerlin]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showCommands && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex((prev) => (prev < filteredCommands.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex((prev) => (prev > 0 ? prev - 1 : filteredCommands.length - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommandSelect(filteredCommands[selectedCommandIndex].command);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      dismissCommands();
    }
  };

  const handleCommandSelect = (command: string) => {
    if (command === '/clear') {
      clearMessages();
      setInput('');
      setShowCommands(false);
      return;
    }

    // All commands auto-trigger immediately
    setInput('');
    setShowCommands(false);
    const slashCmd = SLASH_COMMANDS.find((c) => c.command === command);
    if (slashCmd) {
      handleSend(command, slashCmd.mode);
    }
  };

  // Web Speech API — free, browser-native voice input
  const hasSpeechSupport =
    typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const toggleListening = useCallback(() => {
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    if (!hasSpeechSupport) return;

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return;
    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0][0].transcript;
      setInput((prev) => (prev ? prev + ' ' + transcript : transcript));
      inputRef.current?.focus();
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening, hasSpeechSupport]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const isDisabled = !isPaidUser && !isDemo;
  const isDemoInputDisabled = isDemo && !isPaidUser;
  const hasGreetedRef = useRef(false);

  // Hardcoded greeting for paid users on first mount (includes paid users on demo)
  useEffect(() => {
    if (hasGreetedRef.current || !isPaidUser || messages.length > 0 || autoTrigger) return;
    hasGreetedRef.current = true;

    let greetingText: string;

    if (entityType === 'project' && cards && cards.length > 0) {
      const completedCount = cards.filter((c) => c.status === 'completed').length;
      if (completedCount === 0 && currentCard) {
        greetingText = `Welcome to **${entityName}**! I can see the design and the project has ${cards.length} cards to work through. Let's start with **Card 1 — ${currentCard.title}**. I'm here to guide you step by step!`;
      } else if (completedCount === cards.length) {
        greetingText = `Welcome back to **${entityName}**! All ${cards.length} cards are complete — amazing work! I can help you polish, refactor, or add finishing touches.`;
      } else if (currentCard) {
        greetingText = `Welcome back to **${entityName}**! You've completed ${completedCount}/${cards.length} cards. Let's pick up with **Card ${currentCard.index + 1} — ${currentCard.title}**!`;
      } else {
        greetingText = HARDCODED_GREETINGS.project(entityName);
      }
    } else {
      const getGreeting = HARDCODED_GREETINGS[entityType];
      greetingText = getGreeting
        ? getGreeting(entityName)
        : `Welcome to **${entityName}**! Let's build something great together!`;
    }

    addMessage({
      id: uuidv4(),
      created_at: new Date().toISOString(),
      who: 'Merlin',
      message: greetingText + buildCommandsSection(entityType),
      status: 'received',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-trigger: fire streaming once on mount when autoTrigger is configured
  // Uses [] deps — autoTrigger is stable from props and should only fire once
  useEffect(() => {
    if (!autoTrigger || autoTriggeredRef.current) return;

    // Small delay to let the component fully mount
    const timeoutId = setTimeout(() => {
      autoTriggeredRef.current = true;
      handleSend(autoTrigger.message, autoTrigger.mode as MerlinMode, autoTrigger.bypassAiLimit);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-demo spell: fires once per mount for demo page (Merlin speaks and casts)
  // Clears old messages so each open feels fresh. Uses demoSpellState for index cycling.
  // Flow: preview shows loading overlay → Merlin types → code applied AFTER typing → preview shows magic
  // Uses shared demoSpellCancelledRef/demoSpellTimeoutsRef so handleSend can also cancel in-flight spells.
  const autoDemoFiredRef = useRef(false);
  useEffect(() => {
    if (!isDemo || autoDemoFiredRef.current || autoTrigger) return;

    autoDemoFiredRef.current = true;

    // Cancel any previous spell chain (safety)
    demoSpellCancelledRef.current = true;
    demoSpellTimeoutsRef.current.forEach(clearTimeout);
    demoSpellTimeoutsRef.current = [];
    demoSpellCancelledRef.current = false;

    // Clear old messages so each Merlin open feels fresh
    clearMessages();

    const randomIdx = getRandomSpellIndex(demoSpellState.spellIndex);
    const spell = DEMO_SPELLS[randomIdx];

    // Always restore to original template code (not live editor state)
    const originalCode = merlinContextConfig?.originalCode || { html, css, js };

    // Mark spell as loading (preview shows overlay), NOT active yet (code not applied yet)
    setDemoSpellState(() => ({
      isSpellActive: false,
      isSpellLoading: true,
      savedCode: originalCode,
      spellIndex: randomIdx,
    }));

    const merlinId = uuidv4();
    addMessage({
      id: merlinId,
      created_at: new Date().toISOString(),
      who: 'Merlin',
      message: '',
      status: 'sending',
    });

    // Stream spell text character by character (10ms per char for snappy demo feel)
    let charIndex = 0;
    const streamText = () => {
      if (demoSpellCancelledRef.current) return;
      if (charIndex < spell.text.length) {
        const partial = spell.text.slice(0, charIndex + 1);
        updateMessage(merlinId, { message: partial, status: 'streaming' });
        charIndex++;
        demoSpellTimeoutsRef.current.push(setTimeout(streamText, 10));
      } else {
        if (demoSpellCancelledRef.current) return;
        // Typing done — apply magic code
        const diffs: MerlinDiffInfo[] = [
          { tab: 'html', added: spell.html.split('\n').length, removed: html.split('\n').length },
          { tab: 'css', added: spell.css.split('\n').length, removed: css.split('\n').length },
          { tab: 'js', added: spell.js.split('\n').length, removed: js.split('\n').length },
        ];

        window.dispatchEvent(
          new CustomEvent(MERLIN_STREAMING_EVENT, { detail: { isStreaming: true, diffs } })
        );

        onHtmlChange(spell.html);
        onCssChange(spell.css);
        onJsChange(spell.js);

        updateMessage(merlinId, {
          message: spell.text,
          status: 'received',
          diffs,
          codeChanges: [
            { tab: 'html', action: 'replace', content: spell.html },
            { tab: 'css', action: 'replace', content: spell.css },
            { tab: 'js', action: 'replace', content: spell.js },
          ],
        });

        // Reveal immediately — the 600ms CSS fade-out in DemoPreview covers iframe rebuild time
        setDemoSpellState((prev) => ({ ...prev, isSpellActive: true, isSpellLoading: false }));

        demoSpellTimeoutsRef.current.push(setTimeout(() => {
          if (demoSpellCancelledRef.current) return;
          window.dispatchEvent(
            new CustomEvent(MERLIN_STREAMING_EVENT, { detail: { isStreaming: false, diffs: [] } })
          );
        }, 500));

        // Follow-up message with CTA + Remove Spell (no auto-revert)
        demoSpellTimeoutsRef.current.push(setTimeout(() => {
          if (demoSpellCancelledRef.current) return;
          addMessage({
            id: uuidv4(),
            created_at: new Date().toISOString(),
            who: 'Merlin',
            message: buildDemoFollowUpMessage(isSignedIn, isPaidUser),
            status: 'received',
            showRemoveSpell: true,
          });
        }, 500));
      }
    };

    // Start streaming after a brief delay
    demoSpellTimeoutsRef.current.push(setTimeout(streamText, 300));

    return () => {
      demoSpellCancelledRef.current = true;
      demoSpellTimeoutsRef.current.forEach(clearTimeout);
      demoSpellTimeoutsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus input when panel mounts (Merlin opens)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Hardcoded greeting for free/demo/anon users
  const greeting = useMemo(() => {
    if (messages.length > 0 || isPaidUser) return null;

    if (isDemo) {
      if (!isSignedIn) {
        return DEMO_GREETING;
      }

      // Free or legacy user — they're signed in, so nudge to upgrade
      return "Hey there! ✨ I'm **Merlin** — type anything and watch a little preview of my magic. Upgrade your plan to unleash my full powers! 🪄";
    }

    return `Upgrade your plan to unlock Merlin AI and start building together!`;
  }, [messages.length, isPaidUser, isDemo, isSignedIn]);

  return (
    <VStack height="100%" width="100%" spacing={0} position="relative">
      {/* Messages area */}
      <Box
        ref={scrollRef}
        flex={1}
        width="100%"
        overflowY="auto"
        overflowX="hidden"
        px={4}
        pt={4}
        pb={2}
      >
        <VStack spacing={4} alignItems="stretch">
          {/* Greeting */}
          {greeting && (
            <MerlinMessageComponent
              message={{
                id: 'greeting',
                created_at: '',
                who: 'Merlin',
                message: greeting,
                status: 'received',
              }}
            />
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <MerlinMessageComponent
              key={msg.id}
              message={msg}
              onTabSwitch={handleTabSwitch}
              onCardComplete={entityType === 'project' ? handleCardComplete : undefined}
              completedCardIds={completedViaIds}
              onRemoveSpell={isDemo ? handleRemoveSpell : undefined}
            />
          ))}
        </VStack>
      </Box>

      {/* Slash command palette */}
      {showCommands && (
        <>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={9}
            onClick={dismissCommands}
          />
          <Box
            position="absolute"
            bottom="80px"
            left={4}
            right={4}
            bg="gray.700"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="lg"
            overflow="hidden"
            zIndex={10}
            boxShadow="0 -4px 20px rgba(0,0,0,0.4)"
          >
            {filteredCommands.map((cmd, index) => (
              <Box
                key={cmd.command}
                px={3}
                py={2}
                cursor="pointer"
                bg={index === selectedCommandIndex ? 'whiteAlpha.200' : 'transparent'}
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={() => handleCommandSelect(cmd.command)}
                transition="background 0.1s"
              >
                <HStack spacing={2}>
                  <Text fontSize="small" fontWeight="semibold" color="purple.300">
                    {cmd.command}
                  </Text>
                  <Text fontSize="small" opacity="secondary">
                    {cmd.description}
                  </Text>
                </HStack>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Input area */}
      <Box width="100%" px={4} pb={4} pt={2}>
        {isDisabled ? (
          <Box
            p={3}
            borderRadius="xl"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
            textAlign="center"
          >
            <Text fontSize="small" opacity="secondary">
              Upgrade your plan to unlock Merlin AI&apos;s powers.
            </Text>
          </Box>
        ) : isDemoInputDisabled ? (
          <Box
            p={3}
            borderRadius="xl"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
            textAlign="center"
          >
            <Text fontSize="micro" opacity="secondary" mb={2}>
              Free 7-day trial to chat with Merlin AI
            </Text>
            <Button
              onClick={handleLoginClick}
              scheme="gradient"
              size="sm"
              leftIcon={<Icon type="Github" size={16} />}
            >
              Sign in with GitHub
            </Button>
          </Box>
        ) : isAiLimitReached && !isDemo && !isStreaming ? (
          <Box
            p={3}
            borderRadius="xl"
            bg="rgba(15,10,10,0.94)"
            border="1px solid"
            borderColor="rgba(248,113,113,0.4)"
            textAlign="center"
          >
            <Text fontSize="small" color="#fecaca" mb={2}>
              AI usage limit reached.
            </Text>
            <Box
              as="button"
              onClick={() => {
                if (!hasTokens) {
                  onTokensModalOpen();
                  return;
                }
                redeemMutation.mutate({ rewardType: 'extend-ai-limit' });
              }}
              display="inline-flex"
              alignItems="center"
              gap="6px"
              px={3}
              py={1.5}
              borderRadius="md"
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{ bg: 'whiteAlpha.200', borderColor: 'purple.400' }}
              _active={{ transform: 'scale(0.96)' }}
            >
              {redeemMutation.isPending && redeemMutation.variables?.rewardType === 'extend-ai-limit' ? (
                <Spinner size="xs" color="purple.300" />
              ) : (
                <>
                  <Icon type="Bigtokens" size={14} />
                  <Text fontSize="small" fontWeight="semibold" color="purple.300">
                    {hasTokens ? '+50K tokens' : 'Get BigTokens'}
                  </Text>
                </>
              )}
            </Box>
            <TokensModal isOpen={isTokensModalOpen} onClose={onTokensModalClose} />
          </Box>
        ) : (
          <Box
            borderRadius="xl"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor={isInputFocused ? 'purple.500' : 'whiteAlpha.100'}
            overflow="hidden"
            transition="border-color 0.2s ease"
            position="relative"
          >
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-resize: reset then set to scrollHeight
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 160) + 'px';
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              placeholder="Type a message or / for commands..."
              rows={3}
              resize="none"
              bg="transparent"
              border="none"
              borderRadius="0"
              px={3}
              pt={3}
              pb={1}
              minH="80px"
              maxH="160px"
              overflowY="auto"
              _hover={{}}
              _focus={{ boxShadow: 'none' }}
              maxLength={5000}
              isDisabled={isStreaming}
            />
            {/* Bottom bar with action button */}
            <HStack px={2} pb={2} justifyContent="flex-end" spacing={2}>
              {isStreaming ? (
                <DevSandboxTooltip label="Stop generating">
                  <IconButton
                    aria-label="Stop"
                    icon={<Box w="14px" h="14px" borderRadius="sm" bg="white" />}
                    onClick={cancelStream}
                    variant="ghost"
                    size="md"
                    minW="36px"
                    h="36px"
                    borderRadius="xl"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  />
                </DevSandboxTooltip>
              ) : input.trim() ? (
                <DevSandboxTooltip label="Send message">
                  <IconButton
                    aria-label="Send message"
                    icon={
                      <Box
                        as="svg"
                        viewBox="0 0 24 24"
                        w="20px"
                        h="20px"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" fill="none" />
                      </Box>
                    }
                    onClick={() => handleSend()}
                    variant="ghost"
                    size="md"
                    minW="36px"
                    h="36px"
                    color="white"
                    borderRadius="xl"
                    _hover={{ bg: 'whiteAlpha.100' }}
                    transition="all 0.15s ease"
                  />
                </DevSandboxTooltip>
              ) : hasSpeechSupport ? (
                <DevSandboxTooltip label={isListening ? 'Stop listening' : 'Voice input'}>
                  <IconButton
                    aria-label={isListening ? 'Stop listening' : 'Voice input'}
                    icon={
                      <Box
                        as="svg"
                        viewBox="0 0 24 24"
                        w="20px"
                        h="20px"
                        fill="none"
                        stroke={isListening ? 'var(--chakra-colors-red-400)' : 'currentColor'}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="1" width="6" height="12" rx="3" />
                        <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                        <line x1="12" y1="18" x2="12" y2="23" />
                        <line x1="8" y1="23" x2="16" y2="23" />
                      </Box>
                    }
                    onClick={toggleListening}
                    variant="ghost"
                    size="md"
                    minW="36px"
                    h="36px"
                    color="white"
                    borderRadius="xl"
                    _hover={{ bg: 'whiteAlpha.100' }}
                    transition="all 0.15s ease"
                  />
                </DevSandboxTooltip>
              ) : (
                <DevSandboxTooltip label="Send message">
                  <IconButton
                    aria-label="Send message"
                    icon={
                      <Box
                        as="svg"
                        viewBox="0 0 24 24"
                        w="20px"
                        h="20px"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" fill="none" />
                      </Box>
                    }
                    onClick={() => handleSend()}
                    variant="ghost"
                    size="md"
                    minW="36px"
                    h="36px"
                    borderRadius="xl"
                    isDisabled
                    opacity={0.3}
                    _disabled={{ opacity: 0.3, cursor: 'default' }}
                  />
                </DevSandboxTooltip>
              )}
            </HStack>
            {/* Accent bottom line on focus */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              h="2px"
              bg="purple.500"
              opacity={isInputFocused ? 1 : 0}
              transition="opacity 0.2s ease"
              borderBottomRadius="xl"
            />
          </Box>
        )}

        {/* AI usage indicator - hidden until 70%, hidden when limit reached */}
        {isPaidUser && !isDemo && !isAiLimitReached && aiPercentUsed >= 70 && (
          <HStack justifyContent="center" mt={2} spacing={1}>
            <Text fontSize="micro" color={aiPercentUsed >= 90 ? 'red.300' : 'orange.300'}>
              AI usage: {Math.min(aiPercentUsed, 100)}%
            </Text>
            <Box w="40px" h="3px" bg="whiteAlpha.100" borderRadius="full" overflow="hidden">
              <Box
                w={`${Math.min(aiPercentUsed, 100)}%`}
                h="100%"
                bg={aiPercentUsed >= 90 ? 'red.400' : 'orange.400'}
                borderRadius="full"
              />
            </Box>
          </HStack>
        )}

        {/* AI disclaimer — hidden on demo since the input area already shows trial CTA */}
        {!isDemo && (
          <Text fontSize="micro" opacity="tertiary" textAlign="center" mt={1}>
            Merlin AI can make mistakes. Review code before submitting.
          </Text>
        )}
      </Box>
    </VStack>
  );
};

export default MerlinPanel;
